/* eslint-disable max-params, no-param-reassign -- these linting errors come up constantly with reducers */
import type { PayloadAction } from "@reduxjs/toolkit";
import { createSlice } from "@reduxjs/toolkit";

import type { RootState } from "@/store";
import { useAppDispatch } from "@/store/hooks";
import { extraReducersFactory } from "@/store/reducers/reset";

interface UserPreferencesSliceState {
  isEventBannerVisible: boolean;
  isWeightPrivacyModeEnabled: boolean;
}

const initialState: UserPreferencesSliceState = {
  isEventBannerVisible: false,
  isWeightPrivacyModeEnabled: true,
};

const userPreferencesSlice = createSlice({
  // NOTE: extraReducers is required for resetAllReduxSlices to work as expected
  extraReducers: extraReducersFactory(initialState),
  initialState,
  name: "userPreferences",
  reducers: {
    resetPreferences: () => initialState,
    setIsEventBannerVisible: (state, action: PayloadAction<boolean>) => {
      state.isEventBannerVisible = action.payload;
    },
    setIsWeightPrivacyModeEnabled: (state, action: PayloadAction<boolean>) => {
      state.isWeightPrivacyModeEnabled = action.payload;
    },
  },
});

const {
  resetPreferences: resetUserPreferencesAction,
  setIsEventBannerVisible: setIsEventBannerVisibleAction,
  setIsWeightPrivacyModeEnabled: setIsWeightPrivacyModeEnabledAction,
} = userPreferencesSlice.actions;

const selectUserPreferences = (state: RootState) => state.userPreferences;
const selectIsEventBannerVisible = (state: RootState) =>
  state.userPreferences.isEventBannerVisible;

const useUserPreferencesDispatch = () => {
  const dispatch = useAppDispatch();

  const setIsWeightPrivacyModeEnabled = (isEnabled: boolean) => {
    dispatch(setIsWeightPrivacyModeEnabledAction(isEnabled));
  };

  const setIsEventBannerVisible = (isVisible: boolean) => {
    dispatch(setIsEventBannerVisibleAction(isVisible));
  };

  const resetUserPreferences = () => {
    dispatch(resetUserPreferencesAction());
  };

  return {
    resetUserPreferences,
    setIsEventBannerVisible,
    setIsWeightPrivacyModeEnabled,
  };
};

export type { UserPreferencesSliceState };
export {
  selectIsEventBannerVisible,
  selectUserPreferences,
  setIsEventBannerVisibleAction,
  setIsWeightPrivacyModeEnabledAction,
  useUserPreferencesDispatch,
};
export default userPreferencesSlice.reducer;
